import React, { useState, useEffect } from "react";
import { createStyles, styled, useTheme } from "@material-ui/styles";
import { Button, IconButton, makeStyles, Tab, Tabs, Theme } from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import { injectIntl } from "react-intl";
import { gql, useQuery, useMutation } from "@apollo/client";
import messages from "./PageViewTeam.intl";
import { IStyledArguments } from "../../types/styled-arguments";

import TeamCard from "./TeamCard";
import ScorersCard from "./ScorersCard";
import SquadCard from "./SquadCard";
import TrainingCard from "./TrainingCard";
import LeagueCard from "./LeagueCard";
import GamesChart from "./GamesChart";
import HistoricalCard from "./HistoricalCard";
import PreSeasonCard from "./PreSeasonCard";
import NextMatchCard from "../page-dashboard/NexMatchCard";
import { StatTile, StatTileValue, StatTileLabel } from "../molecules/cards";
import Modal from "@material-ui/core/Modal";
import EditIcon from "@material-ui/icons/EditRounded";

import useIsManager from "../../hooks/useIsManager";
import Typography from "../typography";
import EditTeamNameDialog from "./EditTeamNameDialog";

const TEAM_QUERY = gql`
  query teamDetails($id: ID!) {
    team(id: $id) {
      id
      name
      location
      colorHome
      colorAway
      foundationYear
      coach {
        id
        name
      }
      squad {
        id
        name
        surname
        avatarUrl
        number
        position
        totalGoals
      }
      matches {
        id
        date
        homeTeam {
          id
          name
        }
        isHomeTeamLocal
        awayTeam
        status
        score {
          home
          away
        }
        isWon
        isTied
        isLost
      }
      trainingSetup {
        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      league {
        id
        name
        startDate
        location
      }
    }
    nextMatch(teamId: $id) {
      id
      date
      homeTeam {
        id
        name
      }
      isHomeTeamLocal
      awayTeam
      location
      time
      status
      playersConfirmed {
        id
        name
        surname
      }
    }
    currentUser {
      id
      role
    }
    getHistoricalData(teamId: $id) {
      league {
        name
        startDate
        endDate
      }
      matches {
        isHomeTeamLocal
        homeTeam {
          id
          name
        }
        isHomeTeamLocal
        awayTeam
        status
        location
        date
        time
        score {
          home
          away
        }
        isWon
        isTied
        isLost
      }
    }
  }
`;

const UPDATE_TEAM = gql`
  mutation EditTeam($id: ID!, $input: TeamInput!) {
    editTeam(id: $id, input: $input) {
      ok
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Page shell                                                          */
/* ------------------------------------------------------------------ */

const PageRoot = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  background: theme.palette.background.paper,
}));

const Main = styled("main")(({ theme }: IStyledArguments) => ({
  display: "block",
  background: theme.palette.extra.backgroundGrey,
  paddingBottom: "70px",
  minHeight: "60vh",
}));

/* ------------------------------------------------------------------ */
/* Header — escudo/iniciales + nombre + liga, sin foto de fondo         */
/* ------------------------------------------------------------------ */

const HeaderWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  maxWidth: theme.layout.maxContentWidth,
  margin: "0 auto",
  padding: theme.spacing(4, 4, 0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3, 2, 0),
  },
}));

const HeroContent = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const TeamBadge = styled("div")(({ theme }: IStyledArguments) => ({
  width: 56,
  height: 56,
  minWidth: 56,
  borderRadius: 14,
  background: theme.palette.extra.accentSoft,
  color: theme.palette.primary.dark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.15rem",
  letterSpacing: "0.5px",
}));

const TitleRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const TeamSubtitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.9rem",
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

const EditButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
}));

/* ------------------------------------------------------------------ */
/* Stats row — 3 tiles (jugados / ganados / goles)                     */
/* ------------------------------------------------------------------ */

const StatsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(1.5),
  maxWidth: theme.layout.maxContentWidth,
  margin: "0 auto",
  padding: theme.spacing(3, 4),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5, 2),
  },
}));

/* ------------------------------------------------------------------ */
/* Tabs                                                                 */
/* ------------------------------------------------------------------ */

const TabsWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  maxWidth: theme.layout.maxContentWidth,
  margin: "0 auto",
  padding: theme.spacing(0, 4),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2),
  },
}));

const StyledTabs = styled(Tabs)(({ theme }: IStyledArguments) => ({
  minHeight: 44,
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  "& .MuiTab-root": {
    minHeight: 44,
    minWidth: "auto",
    padding: theme.spacing(1, 0.5),
    marginRight: theme.spacing(3),
    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
}));

const TabPanel = styled("div")(({ theme }: IStyledArguments) => ({
  maxWidth: theme.layout.maxContentWidth,
  margin: "0 auto",
  padding: theme.spacing(3, 4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5, 2),
  },
}));

const DeleteRow = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  paddingTop: theme.spacing(1),
}));

const ManageCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
}));

const ManageRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.9, 2.25),
  opacity: 0.6,
  "& .manage-row-label": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.palette.extra.loss,
  },
  "& svg": {
    color: theme.palette.extra.loss,
  },
}));

const SoonBadge = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.6rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  background: theme.palette.extra.backgroundGrey,
  borderRadius: 6,
  padding: "2px 6px",
}));

const getTeamInitials = (name: string) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const getTotalGoals = (squad: any) => {
  if (!squad) return 0;
  return squad.reduce(
    (acc: number, player: any) => acc + (player.totalGoals || 0),
    0
  );
};

const getNumberOfMatchesWon = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isWon ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

const getNumberOfMatchesDrawn = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isTied ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

const getNumberOfMatchesLost = (matches: any) => {
  if (!matches) return 0;
  return matches
    .map((match: any) => (match && match.isLost ? 1 : 0))
    .reduce((acc: any, current: any) => acc + current, 0);
};

type PagePlayerProps = Required<Pick<RouteComponentProps, "navigate" | "path">>;

const PageViewTeam = injectIntl<{ id: string } & PagePlayerProps>(
  ({ id, intl }) => {
    const { data, refetch } = useQuery(TEAM_QUERY, {
      variables: {
        id,
      },
    });

    const theme = useTheme<Theme>();
    const [team, setTeam] = useState<any>();
    const [nextMatch, setNextMatch] = useState<any>();
    const [historicalData, setHistoricalData] = useState<any>();
    const [currentUser, setCurrentUser] = useState<any>();
    const [editTeam, { data: editData }] = useMutation(UPDATE_TEAM);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
      if (data && data.currentUser) {
        setCurrentUser(data.currentUser);
      }
      if (data && data.team) {
        setTeam(data.team);
      }
      if (data && data.nextMatch) {
        setNextMatch(data.nextMatch);
      }
      if (data && data.getHistoricalData) {
        setHistoricalData(data.getHistoricalData);
      }
    }, [data]);

    const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
      setActiveTab(newValue);
    };

    useEffect(() => {
      if (editData && editData.editTeam.ok === 1) {
        refetch();
      }
    }, [editData]);

    const handleSaveName = async (newName: string): Promise<void> => {
      if (team) {
        editTeam({
          variables: {
            id,
            input: {
              name: newName,
              location: team.location,
              colorHome: team.colorHome,
              colorAway: team.colorAway,
              foundationYear: team.foundationYear,
            },
          },
        });
      }
    };

    return (
      <>
        {team ? (
          <PageRoot>
            <HeaderWrapper>
              <HeroContent>
                <TeamBadge>{getTeamInitials(team.name)}</TeamBadge>
                <div>
                  <TitleRow>
                    <Typography.H4
                      style={{
                        fontWeight: 700,
                        letterSpacing: "-0.5px",
                        margin: 0,
                      }}
                    >
                      {team.name}
                    </Typography.H4>
                    <EditTeamNameDialog
                      title="Edit team name"
                      message="here you can change the team name"
                      callback={handleSaveName}
                      trigger={({ onOpen }: any) => (
                        <EditButton size="small" onClick={onOpen}>
                          <EditIcon fontSize="small" />
                        </EditButton>
                      )}
                    />
                  </TitleRow>
                  {(team.league?.name || team.location) && (
                    <TeamSubtitle>
                      {team.league?.name || team.location}
                    </TeamSubtitle>
                  )}
                </div>
              </HeroContent>
            </HeaderWrapper>

            <StatsRow>
              <StatTile>
                <StatTileValue>{(team.matches && team.matches.length) || 0}</StatTileValue>
                <StatTileLabel>{intl.formatMessage(messages.played)}</StatTileLabel>
              </StatTile>
              <StatTile>
                <StatTileValue>{getNumberOfMatchesWon(team.matches)}</StatTileValue>
                <StatTileLabel>{intl.formatMessage(messages.won)}</StatTileLabel>
              </StatTile>
              <StatTile>
                <StatTileValue>{getTotalGoals(team.squad)}</StatTileValue>
                <StatTileLabel>{intl.formatMessage(messages.goals)}</StatTileLabel>
              </StatTile>
            </StatsRow>

            <TabsWrapper>
              <StyledTabs value={activeTab} onChange={handleTabChange}>
                <Tab label={intl.formatMessage(messages.squad)} />
                <Tab label={intl.formatMessage(messages.tabCalendar)} />
                <Tab label={intl.formatMessage(messages.tabStats)} />
              </StyledTabs>
            </TabsWrapper>

            <Main>
              {activeTab === 0 && (
                <TabPanel>
                  <SquadCard
                    key={`squad-${team.id}`}
                    id={team.id}
                    team={team}
                    currentUser={currentUser}
                  />
                </TabPanel>
              )}

              {activeTab === 1 && (
                <TabPanel>
                  {nextMatch ? (
                    <NextMatchCard match={nextMatch} />
                  ) : (
                    <Typography.Subtitle1 style={{ color: theme.palette.text.secondary }}>
                      {intl.formatMessage(messages.noMatchesYet)}
                    </Typography.Subtitle1>
                  )}
                  <TrainingCard
                    key={`training-${team.id}`}
                    id={team.id}
                    team={team}
                    currentUser={currentUser}
                  />
                  {currentUser && useIsManager(currentUser.role) && (
                    <PreSeasonCard
                      key={`preseason-${team.id}`}
                      id={team.id}
                      team={team}
                      currentUser={currentUser}
                    />
                  )}
                  {currentUser && (
                    <LeagueCard
                      key={`league-${team.id}`}
                      id={team.id}
                      team={team}
                      currentUser={currentUser}
                    />
                  )}
                </TabPanel>
              )}

              {activeTab === 2 && (
                <TabPanel>
                  {team && team.matches && team.matches.length > 0 && (
                    <GamesChart
                      data={[
                        {
                          id: "lost",
                          label: `${intl.formatMessage(messages.lost)}`,
                          value: `${getNumberOfMatchesLost(team.matches)}`,
                          color: "hsl(10, 70%, 56%)",
                        },
                        {
                          id: "drawn",
                          label: `${intl.formatMessage(messages.draw)}`,
                          value: `${getNumberOfMatchesDrawn(team.matches)}`,
                          color: "hsl(210, 70%, 50%)",
                        },
                        {
                          id: "won",
                          label: `${intl.formatMessage(messages.won)}`,
                          value: `${getNumberOfMatchesWon(team.matches)}`,
                          color: "#77dd91",
                        },
                      ]}
                    />
                  )}
                  <TeamCard key={team.id} id={team.id} team={team} />
                  <ScorersCard key={`scorers-${team.id}`} id={team.id} team={team} />
                  {historicalData && <HistoricalCard data={historicalData} />}
                </TabPanel>
              )}

              {currentUser && useIsManager(currentUser.role) && (
                <TabPanel style={{ paddingTop: 0 }}>
                  <ManageCard>
                    <ManageRow>
                      <span className="manage-row-label">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6" />
                        </svg>
                        {intl.formatMessage(messages.deleteTeam)}
                      </span>
                      <SoonBadge>{intl.formatMessage(messages.comingSoon)}</SoonBadge>
                    </ManageRow>
                  </ManageCard>
                </TabPanel>
              )}
            </Main>
          </PageRoot>
        ) : (
          <div>Loading... </div>
        )}
      </>
    );
  }
);

export default PageViewTeam;
